/**
 * "What this chunk says" digest — an optional AI enrichment shown at the top of
 * the source drawer: a one-paragraph summary + a 2-up key-facts grid. Both are
 * optional; the component renders nothing when neither is present, so the drawer
 * degrades gracefully to the readable full text below it.
 */
import { Sparkles } from "lucide-react";
import type { KbKeyFact } from "../types";

export function ChunkDigest({ summary, keyFacts }: { summary?: string; keyFacts?: KbKeyFact[] }) {
	const hasFacts = keyFacts != null && keyFacts.length > 0;
	if (!summary && !hasFacts) return null;

	return (
		<div className="border-b border-hairline bg-surface-1/50 px-5 py-4">
			{summary ? (
				<>
					<div className="mb-1.5 flex items-center gap-1.5 text-primary-hover">
						<Sparkles className="size-3.5" />
						<span className="text-eyebrow uppercase">What this chunk says</span>
					</div>
					<p className="text-body-sm leading-relaxed text-ink-muted">{summary}</p>
				</>
			) : null}

			{hasFacts ? (
				<div className="mt-3 grid grid-cols-2 gap-2">
					{keyFacts.map((fact) => (
						<div
							key={fact.label}
							className="flex items-baseline justify-between gap-2 rounded-md border border-hairline bg-surface-2 px-2.5 py-1.5"
						>
							<span className="text-meta text-ink-subtle">{fact.label}</span>
							<span className="truncate text-body-sm font-medium tabular-nums text-foreground">
								{fact.value}
							</span>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}
